import React from 'react';
import { bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { propTypes } from '../../util/types';
import { withViewport } from '../../util/contextHelpers';
import { sendCallMeBackRequest } from '../../ducks/callMeBack.duck';
import {
  MARKETING_MAX_RETURN_VALUES,
  MAX_TRAILER_ANNUAL_RETURN_DISPLAY,
} from '../../marketplace-custom-config';
import config from '../../config';
import {
  Page,
  SectionContactUs,
  SectionForOwnersBenefits,
  SectionForOwnersFeatures,
  SectionForOwnersHero,
  SectionForOwnersSteps,
  SectionForOwnersTestimonials,
  SectionForOwnersGuarantee,
  FAQ,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '..';

import facebookImage from '../../assets/lth-og.jpg';
import twitterImage from '../../assets/lth-twitter.jpg';
import css from './ForOwnersPage.module.css';

export const ForOwnersPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    isAuthenticated,
    viewport,
    currentUser = null,
    onSubmitCallMeBack,
    submitInProgress,
    callMeBackError,
    callMeBackSuccess,
    currentUserListing = null,
    currentUserListingFetched = false,
  } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'ForOwnersPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'ForOwnersPage.schemaDescription' },
    { siteTitle, ...MARKETING_MAX_RETURN_VALUES }
  );
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 1024, height: 512 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionForOwnersHero className={css.hero} history={history} location={location} />
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContentFirstChild}>
                <h2 className={css.subheading}>Make extra cash with your trailer!</h2>
                <div className={css.intro}>
                  <p>
                    It's pretty convenient having your own trailer but you don't generally need to
                    use it every day. So why not join the hundreds of Australian trailer owners who
                    are earning up to {MAX_TRAILER_ANNUAL_RETURN_DISPLAY} per year on
                    localtrailerhire.com.au? Create a great side income for yourself and provide a
                    valuable service to your community!
                  </p>
                </div>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersBenefits />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersSteps isAuthenticated={isAuthenticated} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersTestimonials />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersFeatures viewport={viewport} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersGuarantee />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.subheading}>Frequently asked questions</h2>
                <FAQ category={'listing'} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionContactUs
                  onSubmitCallMeBack={onSubmitCallMeBack}
                  submitInProgress={submitInProgress}
                  callMeBackError={callMeBackError}
                  callMeBackSuccess={callMeBackSuccess}
                  currentUser={currentUser}
                  sectionTitle="Contact us"
                />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ForOwnersPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  onSubmitCallMeBack: func.isRequired,
  submitInProgress: bool.isRequired,
  callMeBackError: propTypes.error,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, currentUserListing, currentUserListingFetched } = state.user;
  const { isAuthenticated } = state.Auth;
  const { submitInProgress, callMeBackError, callMeBackSuccess } = state.callMeBack;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    currentUserListing,
    currentUserListingFetched,
    isAuthenticated,
    submitInProgress,
    callMeBackError,
    callMeBackSuccess,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmitCallMeBack: (name, phoneNumber, reasonForCallBack, otherInfo, email) =>
    dispatch(sendCallMeBackRequest(name, phoneNumber, reasonForCallBack, otherInfo, email)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ForOwnersPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(ForOwnersPageComponent);

export default ForOwnersPage;
